import { IBaseAlertProps } from "@components/alert/Alert.types";
import { Button, ButtonGroup } from "@components/button";
import Dialog from "@components/dialog";
import FileUploader from "@components/fileUploader";
import File from "@components/fileUploader/File";
import { fileNameToMime } from "@components/fileUploader/File.utils";
import { FileToolbarItem, getFileViewer, IFileViewerProps } from "@components/fileViewers/FileViewers.utils";
import { IHeaderIcon } from "@components/header/Header";
import { ActionType, addEmptyLineItem, ISmartFastEntriesActionEvent } from "@components/smart/smartFastEntryList";
import { ISmartFieldChange } from "@components/smart/smartField/SmartField";
import SmartFormDeleteButton from "@components/smart/smartFormDeleteButton/SmartFormDeleteButton";
import { Pane, SplitLayout } from "@components/splitLayout";
import BindingContext, { createPath, IEntity } from "@odata/BindingContext";
import { expandEnumProps, getNewItemsMaxId, saveEntity } from "@odata/Data.utils";
import {
    IPrAnnualTaxSettlementRequestEntity,
    IPrAtsrFormSectionOtherEmploymentRecordsEntity,
    IPrTaxpayerDeclarationEntity,
    PrAnnualTaxSettlementRequestEntity,
    PrAtsrFormSectionDetailMortgageLocationEntity,
    PrAtsrFormSectionDetailMortgageOtherPeopleEntity,
    PrAtsrFormSectionDetailPartnerTaxCreditEntity,
    PrAtsrFormSectionEntity,
    PrAtsrFormSectionMortgageInterestEntity,
    PrAtsrFormSectionOtherEmploymentRecordsEntity,
    PrAtsrFormSectionPartnerTaxCreditEntity,
    PrTaxpayerDeclarationChildEntity
} from "@odata/GeneratedEntityTypes";
import {
    PrAnnualTaxSettlementRequestResultTypeCode,
    PrAnnualTaxSettlementRequestStatusTypeCode,
    PrTaxpayerDeclarationStatusCode,
    PrTaxpayerDeclarationTypeCode
} from "@odata/GeneratedEnums";
import { BatchRequest, isBatchResultOk } from "@odata/OData";
import { parseError } from "@odata/ODataParser";
import { isUserEmployee } from "@pages/admin/users/Users.utils";
import { setMatchingNumberRange } from "@pages/numberRange/NumberRange.utils";
import {
    setDiscountValidityAfterLoad,
    setDiscountValidityBeforeSave
} from "@pages/payroll/annualTaxSettlementRequest/AdditionalTaxDeclaration";
import {
    AnnualTaxSettlementFormViewAction,
    hasAdditionalTaxPayerDeclarationPath,
    mortgageInterestHasOtherPeoplePath,
    mortgageInterestLocationDescPath,
    otherEmploymentsGroupId,
    sections
} from "@pages/payroll/annualTaxSettlementRequest/AnnualTaxSettlementRequest.def";
import { FileNameWrapper } from "@pages/payroll/annualTaxSettlementRequest/AnnualTaxSettlementRequest.styles";
import { loadPossibleEmployeesForNewAnnualTaxSettlement } from "@pages/payroll/annualTaxSettlementRequest/AnnualTaxSettlementRequest.utils";
import OtherEmploymentFormView from "@pages/payroll/annualTaxSettlementRequest/OtherEmploymentFormView";
import {
    commonTaxpayerDeclarationOnChange,
    loadPrTaxDiscountTypes,
    taxpayerDeclarationOnAfterLoad,
    taxpayerDeclarationOnBeforeSave
} from "@pages/payroll/taxpayerDeclaration/TaxpayerDeclaration.utils";
import customFetch from "@utils/customFetch";
import { isNewEntity } from "@utils/definition.utils";
import FileStorage from "@utils/FileStorage";
import { isDefined, isObjectEmpty } from "@utils/general";
import { cloneDeep } from "lodash";
import React, { ReactElement } from "react";

import { FILES_API_URL, REST_API_URL } from "../../../constants";
import { withPermissionContext } from "../../../contexts/permissionContext/withPermissionContext";
import { PageViewMode, PaneStatus, Status } from "../../../enums";
import { TRecordAny } from "../../../global.types";
import TestIds from "../../../testIds";
import { getUtcDayjs } from "../../../types/Date";
import { FileViewStyled } from "../../../views/fileView/FileView.styles";
import { getAlertFromError, getFormGroup } from "../../../views/formView/Form.utils";
import {
    IContextInitArgs,
    IFormStorageDefaultCustomData,
    IFormStorageSaveResult,
    ISaveArgs
} from "../../../views/formView/FormStorage";
import { FormViewForExtend, IFormViewProps } from "../../../views/formView/FormView";
import PlugAndPlayForm from "../../../views/formView/PlugAndPlayForm";
import { StyledView } from "../../../views/View.styles";
import { getDefinition as getOtherEmploymentDefinition } from "./OtherEmploymentDialog.def";

const GET_ANNUAL_TAXPAYER_INFO_PATH = `${REST_API_URL}/PrPayroll/GetAnnualTaxpayerDeclaration`;

export interface IPrAnnualTaxSettlementRequestCustomData extends IFormStorageDefaultCustomData {
    annualTaxPayerInfo: IPrTaxpayerDeclarationEntity;
    possibleEmployeesIds: Set<number>;
}

interface IState {
    otherEmploymentDialogBc?: BindingContext;
    isUploadBusy?: boolean;
    paneStatus?: PaneStatus[];
}

class AnnualTaxSettlementRequestFormView extends FormViewForExtend<IPrAnnualTaxSettlementRequestEntity, IFormViewProps<IPrAnnualTaxSettlementRequestEntity, IPrAnnualTaxSettlementRequestCustomData>, IState> {
    static defaultProps = {
        formProps: {
            shouldHideVariant: true
        }
    };

    state: IState = {
        otherEmploymentDialogBc: null,
        isUploadBusy: false,
        paneStatus: [PaneStatus.Normal, PaneStatus.Normal]
    };

    get defaultYear(): number {
        return getUtcDayjs().get("y") - 1;
    }

    getAdditionalLoadPromise(args: IContextInitArgs): (Promise<unknown | void>)[] {
        const additionalLoadPromises = [];
        additionalLoadPromises.push(loadPrTaxDiscountTypes(this.props.storage));

        if (isNewEntity({ storage: this.props.storage })) {
            additionalLoadPromises.push(loadPossibleEmployeesForNewAnnualTaxSettlement(this.props.storage, this.defaultYear));
        }

        additionalLoadPromises.push(...super.getAdditionalLoadPromise(args));
        return additionalLoadPromises;
    }

    get isBeingFilledAndUserIsNotEmployee(): boolean {
        if (isNewEntity({ storage: this.props.storage })) {
            return false;
        }
        return this.entity.StatusCode === PrAnnualTaxSettlementRequestStatusTypeCode.FormIsBeingFilled && !isUserEmployee(this.props.storage.context);
    }

    async onAfterLoad(): Promise<void> {
        const { storage } = this.props;
        if (isNewEntity({ storage })) {
            storage.setValueByPath(PrAnnualTaxSettlementRequestEntity.ResultCode, PrAnnualTaxSettlementRequestResultTypeCode.Requested);
            storage.setValueByPath(PrAnnualTaxSettlementRequestEntity.StatusCode, PrAnnualTaxSettlementRequestStatusTypeCode.InProgress);
            storage.setValueByPath(PrAnnualTaxSettlementRequestEntity.Year, this.defaultYear);
            await setMatchingNumberRange(storage);
        } else {
            if (this.entity.MortgageInterest?.OtherPeople?.length) {
                storage.setValueByPath(mortgageInterestHasOtherPeoplePath, true);
            }
        }

        await this.getTaxPayerInfo();
        if (isDefined(this.entity.YearlyTaxpayerDeclaration?.Id)) {
            storage.setValueByPath(hasAdditionalTaxPayerDeclarationPath, true);
            taxpayerDeclarationOnAfterLoad(this.entity.YearlyTaxpayerDeclaration, storage);
            setDiscountValidityAfterLoad(this.entity.YearlyTaxpayerDeclaration, storage);
        }

        // https://solitea-cz.atlassian.net/browse/DEV-43363
        // storage.data.disabled = this.isBeingFilledAndUserIsNotEmployee; // what if storage is disabled for other reasons? todo: check if this is correct

        return super.onAfterLoad();
    }

    getTaxPayerInfo = async (): Promise<void> => {
        const storage = this.props.storage;
        if (!this.entity.Employee || !this.entity.Year) {
            return;
        }
        let errorAlert: IBaseAlertProps = null;
        try {
            const response = await customFetch(`${GET_ANNUAL_TAXPAYER_INFO_PATH}/${this.entity.Year}/${this.entity.Employee.Id}`);
            const data = await response.json();
            if (response.ok) {
                if (data) {
                    // todo BE sends Id as 0 for every child, we need to fix it on BE side, change rest api to oData action
                    //  as it's fixed out of the box there, for now we fix it here
                    data.Children?.forEach((child: any, index: number) => {
                        child.Id = index + 1;
                    });
                    data.Discounts?.forEach((discount: any, index: number) => {
                        discount.Id = index + 1;
                    });

                    expandEnumProps(data, storage.data.bindingContext.navigate(PrAnnualTaxSettlementRequestEntity.YearlyTaxpayerDeclaration));
                    data.Employee = this.entity.Employee;
                    storage.setCustomData({ annualTaxPayerInfo: data });
                }
            } else {
                const error = parseError(data);
                errorAlert = getAlertFromError(error);
            }
        } catch (error) {
            errorAlert = getAlertFromError(error);
        }
        if (errorAlert) {
            storage.setFormAlert(errorAlert);
        }
    };

    answerRequestForEmployee = async (doesRequest: boolean): Promise<void> => {
        if (doesRequest) {
            await this.changeStatus({
                ResultCode: PrAnnualTaxSettlementRequestResultTypeCode.Requested,
                // StatusCode: PrAnnualTaxSettlementRequestStatusTypeCode.FormIsBeingFilled
                StatusCode: PrAnnualTaxSettlementRequestStatusTypeCode.InProgress // temporary https://solitea-cz.atlassian.net/browse/DEV-44563
            });
        } else {
            await this.changeStatus({
                ResultCode: PrAnnualTaxSettlementRequestResultTypeCode.NotRequested,
                StatusCode: PrAnnualTaxSettlementRequestStatusTypeCode.NotRequested
            });
        }
    };

    fillForEmployee = async (): Promise<void> => {
        await this.changeStatus({ StatusCode: PrAnnualTaxSettlementRequestStatusTypeCode.InProgress });
    };

    handleOtherEmployeeIsActiveChange = (e: ISmartFieldChange): void => {
        if (e.bindingContext.getParent().getPath() !== PrAnnualTaxSettlementRequestEntity.OtherEmployments || e.bindingContext.getPath() !== PrAtsrFormSectionEntity.IsActive) {
            return;
        }
        //
        // if (!this.entity.OtherEmployments?.OtherEmployments?.length) {
        //     const storage = this.props.storage;
        //     const newItem = addEmptyLineItem({
        //         storage,
        //         bindingContext: e.bindingContext.getParent().navigate(PrAtsrFormSectionOtherEmploymentsEntity.OtherEmployments),
        //         newItemsCount: 1,
        //         index: 1,
        //         columns: [],
        //         context: storage.context
        //     });
        //     storage.setValue(e.bindingContext.getParent().navigate(PrAnnualTaxSettlementRequestEntity.OtherEmployments), [newItem]);
        // }
    };

    handleYearChange = async (e: ISmartFieldChange): Promise<void> => {
        if (e.bindingContext.getPath() === PrAnnualTaxSettlementRequestEntity.Year && e.triggerAdditionalTasks) {
            this.props.storage.setBusy(true);
            await loadPossibleEmployeesForNewAnnualTaxSettlement(this.props.storage, Number(e.value));
            this.props.storage.refresh(true);
            this.props.storage.setBusy(false);
        }
    };

    changeStatus = async (entity: Partial<IPrAnnualTaxSettlementRequestEntity>): Promise<void> => {
        const storage = this.props.storage;
        storage.setBusy(true);
        const batch: BatchRequest = this.props.storage.oData.batch();
        batch.beginAtomicityGroup("resultChange");
        saveEntity({
            bindingContext: storage.data.bindingContext,
            entity,
            batch
        });
        const results = await batch.execute();

        if (isBatchResultOk(results[0])) {
            await storage.reload();
            this.props.onTableRefreshNeeded?.();
        } else {
            this.props.storage.setFormAlert({
                status: Status.Error,
                title: this.props.storage.t("Common:Errors.ErrorHappened")
            });
            storage.setBusy(false);
        }
    };

    getHeaderIcons(): IHeaderIcon[] {
        const icons = super.getHeaderIcons();
        if (this.props.storage.data.bindingContext.isNew()) {
            return icons;
        }
        // if (this.entity.ResultCode === PrAnnualTaxSettlementRequestResultTypeCode.Requested && this.entity.StatusCode === PrAnnualTaxSettlementRequestStatusTypeCode.FormIsBeingFilled) {
        //     icons.push({
        //         id: "fillForEmployee",
        //         onClick: () => {
        //             this.fillForEmployee();
        //         },
        //         ignoreMasterDisabled: this.isBeingFilledAndUserIsNotEmployee,
        //         label: this.props.storage.t("AnnualTaxSettlementRequest:Form.FillForEmployee"),
        //         iconName: "FillForEmployee"
        //     });
        // }
        if (this.entity.ResultCode !== PrAnnualTaxSettlementRequestResultTypeCode.Requested) {
            icons.push({
                id: "answerRequestYes",
                onClick: () => {
                    this.answerRequestForEmployee(true);
                },
                label: this.props.storage.t("AnnualTaxSettlementRequest:Form.AnswerRequestYes"),
                iconName: "Request"
            });
        }
        if (this.entity.ResultCode !== PrAnnualTaxSettlementRequestResultTypeCode.NotRequested &&
            this.entity.StatusCode !== PrAnnualTaxSettlementRequestStatusTypeCode.Done) {
            icons.push({
                id: "answerRequestNo",
                ignoreMasterDisabled: this.isBeingFilledAndUserIsNotEmployee,
                onClick: () => {
                    this.answerRequestForEmployee(false);
                },
                label: this.props.storage.t("AnnualTaxSettlementRequest:Form.AnswerRequestNo"),
                iconName: "DoesNotRequest"
            });
        }
        return icons;
    }

    validateAndLock = async (): Promise<void> => {
        // const wasNew = isNewEntity({ storage: this.props.storage });
        const data = this.onBeforeSave();
        // if (!wasNew) {
        data.StatusCode = PrAnnualTaxSettlementRequestStatusTypeCode.Done;
        // }
        await this.save({ data });
    };

    unlock = async (): Promise<void> => {
        await this.changeStatus({
            StatusCode: PrAnnualTaxSettlementRequestStatusTypeCode.InProgress,
            ResultCode: PrAnnualTaxSettlementRequestResultTypeCode.Requested
        });
    };

    saveDraft = async (): Promise<void> => {
        await this.save();
    };

    saveFromFilling = async (): Promise<void> => {
        const data = this.onBeforeSave();
        data.StatusCode = PrAnnualTaxSettlementRequestStatusTypeCode.InProgress;
        await this.save({ data });
    };

    return = async (): Promise<void> => {
        await this.changeStatus({
            StatusCode: PrAnnualTaxSettlementRequestStatusTypeCode.FormIsBeingFilled,
            ResultCode: PrAnnualTaxSettlementRequestResultTypeCode.Requested
        });
    };

    renderButtons(): React.ReactElement {
        const storage = this.props.storage;
        const isFillingForm = PrAnnualTaxSettlementRequestStatusTypeCode.FormIsBeingFilled === this.entity.StatusCode;
        const isBeingVerified = PrAnnualTaxSettlementRequestStatusTypeCode.InProgress === this.entity.StatusCode;
        const isVerified = PrAnnualTaxSettlementRequestStatusTypeCode.Done === this.entity.StatusCode;
        const isNew = this.props.storage.data.bindingContext.isNew();
        const saveTranslationKey = isNew ? "AnnualTaxSettlementRequest:Form.SaveNew" : "AnnualTaxSettlementRequest:Form.ValidateAndLock";

        return <ButtonGroup wrap={"wrap"}>
            <Button hotspotId={"formCancel"}
                    onClick={this.handleFormCancel}
                    isTransparent>{storage.t("Common:General.Cancel")}</Button>
            {this.isDeletable && <SmartFormDeleteButton storage={storage}
                                                        ignoreMasterDisabled={this.isBeingFilledAndUserIsNotEmployee}
                                                        onClick={this.handleDelete}/>}
            {/*{isFillingForm && !this.isBeingFilledAndUserIsNotEmployee &&*/}
            {/*    <Button onClick={this.saveFromFilling}>{storage.t("Common:General.Save")}</Button>}*/}
            {/*{(isBeingVerified || this.isBeingFilledAndUserIsNotEmployee) && <>*/}
            {!isVerified && <>
                <Button onClick={this.saveDraft}
                        isDisabled={storage.isDisabled}
                        isTransparent>{storage.t("AnnualTaxSettlementRequest:Form.SaveDraft")}</Button>
                {/*<Button onClick={this.return}*/}
                {/*        isDisabled={storage.isDisabled}*/}
                {/*        isTransparent>{storage.t("AnnualTaxSettlementRequest:Form.Return")}</Button>*/}
                <Button
                    isDisabled={storage.isDisabled}
                    onClick={this.validateAndLock}>{storage.t(saveTranslationKey)}</Button>
            </>}
            {isVerified && <Button onClick={this.unlock}>{storage.t("AnnualTaxSettlementRequest:Form.Unlock")}</Button>}
        </ButtonGroup>;
    }

    handleGroupToggleChange(e: ISmartFieldChange): void {
        if (e.bindingContext.getPath() === PrAtsrFormSectionEntity.IsActive) {
            const storage = this.props.storage;
            if (e.bindingContext.getParent().getPath() === PrAnnualTaxSettlementRequestEntity.MortgageInterest && e.value === true) {
                if (!this.entity.MortgageInterest?.Locations?.length) {
                    const locationsBc = e.bindingContext.getParent().navigate(PrAtsrFormSectionMortgageInterestEntity.Locations);
                    const newItem = addEmptyLineItem({
                        storage,
                        bindingContext: locationsBc,
                        newItemsCount: 1,
                        index: 1,
                        columns: [],
                        context: storage.context
                    });
                    storage.setValue(locationsBc, [newItem]);
                }
            }
            storage.refresh();
        }
    }

    handleMortgageInterestHasOtherChange = (e: ISmartFieldChange): void => {
        if (e.bindingContext.getPath() !== mortgageInterestHasOtherPeoplePath) {
            return;
        }

        const storage = this.props.storage;
        const otherPeopleBc = storage.data.bindingContext.navigate(PrAnnualTaxSettlementRequestEntity.MortgageInterest).navigate(PrAtsrFormSectionMortgageInterestEntity.OtherPeople);
        if (e.value === true && !this.entity.MortgageInterest?.OtherPeople?.length) {
            const def = getFormGroup({ form: storage.data.definition }, "MortgageInterest2");
            const newItem = addEmptyLineItem({
                storage,
                bindingContext: otherPeopleBc,
                newItemsCount: 1,
                index: 1,
                columns: def?.lineItems?.columns ?? [],
                context: storage.context
            });
            storage.setValue(otherPeopleBc, [newItem]);
        } else if (e.value === false) {
            storage.setValue(otherPeopleBc, []);
        }
        storage.refresh();

    };

    handleIdentifierCodeChange = (e: ISmartFieldChange): void => {
        if (e.bindingContext.getFullPath(true) !== createPath(PrAnnualTaxSettlementRequestEntity.PartnerTaxCredit, PrAtsrFormSectionPartnerTaxCreditEntity.Detail, PrAtsrFormSectionDetailPartnerTaxCreditEntity.IdentifierCode)) {
            this.props.storage.refreshGroupByKey("PartnerTaxCredit");
        }
    };

    handleAdditionalTaxDeclarationChange(e: ISmartFieldChange): void {
        if (e.bindingContext.getPath() !== hasAdditionalTaxPayerDeclarationPath) {
            return;
        }
        if (e.value === false) {
            // delete additional declaration
        }

        if (e.value === true) {
            // create additional declaration with default values
        }
        this.props.storage.refresh();
    }

    handleMortgageLocationAmountChange(e: ISmartFieldChange): void {
        if (e.bindingContext.getNavigationPath(true) !== createPath(PrAnnualTaxSettlementRequestEntity.MortgageInterest, PrAtsrFormSectionMortgageInterestEntity.Locations, PrAtsrFormSectionDetailMortgageLocationEntity.Amount)) {
            return;
        }
        this.props.storage.refreshField(mortgageInterestLocationDescPath);
    }

    async handleEmployeeChange(e: ISmartFieldChange): Promise<void> {
        if (e.bindingContext.getPath() === PrAnnualTaxSettlementRequestEntity.Employee && e.triggerAdditionalTasks) {
            this.props.storage.setBusy(true);
            await this.getTaxPayerInfo();
            this.props.storage.refresh(true);
            this.props.storage.setBusy(false);
        }
    }

    handleChange(e: ISmartFieldChange): void {
        const storage = this.props.storage;
        storage.handleChange(e);

        commonTaxpayerDeclarationOnChange(e, storage, PrAnnualTaxSettlementRequestEntity.YearlyTaxpayerDeclaration);
        this.handleOtherEmployeeIsActiveChange(e);
        this.handleMortgageInterestHasOtherChange(e);
        this.handleIdentifierCodeChange(e);
        this.handleEmployeeChange(e);
        this.handleYearChange(e);
        this.handleAdditionalTaxDeclarationChange(e);

        this.handleGroupToggleChange(e);
        const shouldUpdate = e.triggerAdditionalTasks !== false;
        storage.refreshFields(shouldUpdate);
    }

    handleLineItemsChange(e: ISmartFieldChange) {
        const storage = this.props.storage;
        super.handleLineItemsChange(e);
        this.handleMortgageLocationAmountChange(e);
        if (e.bindingContext.getPath() === PrAtsrFormSectionDetailMortgageLocationEntity.PropertyAcquisition) {
            storage.refreshFields(true);
        }
        if ((e.bindingContext.getPath() === PrAtsrFormSectionDetailMortgageOtherPeopleEntity.IdentifierCode && e.triggerAdditionalTasks) ||
            e.bindingContext.getPath() === PrTaxpayerDeclarationChildEntity.IncludedMonths) {
            storage.refresh();
        }
    }


    handleLineItemsAction = async (args: ISmartFastEntriesActionEvent) => {
        if (args.customActionType === AnnualTaxSettlementFormViewAction.CopyOtherEmployementData) {
            this.setState({
                otherEmploymentDialogBc: args.bindingContext
            });
        } else if (args.groupId === otherEmploymentsGroupId && args.actionType === ActionType.Add) {
            const maxId = (getNewItemsMaxId(this.entity.OtherEmployments?.OtherEmployments) || 0) + 1;
            const newItemBc = args.bindingContext.addKey(maxId, true);
            this.setState({
                otherEmploymentDialogBc: newItemBc
            });
        } else {
            this.props.storage.handleLineItemsAction(args);
        }
        this.props.storage.refresh();
    };

    onBeforeSave(): IPrAnnualTaxSettlementRequestEntity {
        const { storage } = this.props;
        const data = cloneDeep(this.entity);
        const isNew = this.props.storage.data.bindingContext.isNew();

        if (isNew) {
            // set default values for new entity
            if (isObjectEmpty(data.PartnerTaxCredit.DetailZtp)) {
                data.PartnerTaxCredit.DetailZtp = { IsActive: false };
            }
            // data.StatusCode = PrAnnualTaxSettlementRequestStatusTypeCode.FormIsBeingFilled;
        }

        for (const section of sections) {
            if ((data[section] as IEntity)?.IsActive === false) {
                // to be sure that inactive sections will be created in database
                for (const key in (data[section] as TRecordAny)) {
                    if (data[section].hasOwnProperty(key) && key !== "IsActive" && key !== "Id") {
                        // @ts-ignore
                        data[section][key] = null;
                    }
                }
            }
        }

        if (!storage.getValueByPath(hasAdditionalTaxPayerDeclarationPath)) {
            delete data.YearlyTaxpayerDeclaration;
        } else {
            if (isObjectEmpty(data.YearlyTaxpayerDeclaration)) {
                data.YearlyTaxpayerDeclaration = {} as IPrTaxpayerDeclarationEntity;
            }
            data.YearlyTaxpayerDeclaration.StatusCode = PrTaxpayerDeclarationStatusCode.N_A;
            data.YearlyTaxpayerDeclaration.Employee = data.Employee;
            data.YearlyTaxpayerDeclaration.Year = data.Year;
            data.YearlyTaxpayerDeclaration.DateApplyFrom = getUtcDayjs().set("year", data.Year).startOf("year").toDate();
            data.YearlyTaxpayerDeclaration = taxpayerDeclarationOnBeforeSave(data.YearlyTaxpayerDeclaration, this.props.storage, PrAnnualTaxSettlementRequestEntity.YearlyTaxpayerDeclaration);
            setDiscountValidityBeforeSave(data.YearlyTaxpayerDeclaration, storage);
            data.YearlyTaxpayerDeclaration.TypeCode = PrTaxpayerDeclarationTypeCode.Yearly;
        }
        return data;
    }

    handleOtherEmploymentDialogClose = (): void => {
        this.setState({ otherEmploymentDialogBc: null });
        this.forceUpdate();
    };

    handleOtherEmploymentDialogConfirm = (data: IEntity): void => {
        const bindingContext = this.state.otherEmploymentDialogBc;
        const origData = this.props.storage.getValue(bindingContext);
        this.props.storage.setValue(bindingContext, {
            ...origData,
            ...data
        });
        this.handleOtherEmploymentDialogClose(); // TODO: temporary for development, editable window should remain open after confirm
    };

    setPaneStatus = (paneStatus: PaneStatus[]): void => {
        this.setState({ paneStatus });
        this.forceUpdate();
    };

    handlePaneStatusChange = (paneStatus: PaneStatus[]) => {
        this.setPaneStatus(paneStatus);
    };

    handleNewFiles = async (files: File[]): Promise<void> => {
        this.setState({ isUploadBusy: true });
        this.forceUpdate();

        const fileMetadataPromises = files.map((file) => {
            return FileStorage.upload({ file, name: file.name });
        });
        const filesMetadata = await Promise.all(fileMetadataPromises);
        const newFiles = filesMetadata.map((file) => ({
            File: file,
            Id: file.Id
        }));

        const attachmentsBc = this.state.otherEmploymentDialogBc.navigate(PrAtsrFormSectionOtherEmploymentRecordsEntity.Attachment);

        this.props.storage.setValue(attachmentsBc, {
            Description: newFiles[0].File.Name,
            ...newFiles[0]
        });

        this.setState({ isUploadBusy: false });
        this.forceUpdate();
    };

    handleFileRemove = (): void => {
        const attachmentsBc = this.state.otherEmploymentDialogBc.navigate(PrAtsrFormSectionOtherEmploymentRecordsEntity.Attachment);
        this.props.storage.setValue(attachmentsBc, null);
        this.forceUpdate();
    };

    renderOtherEmploymentDialog() {
        if (!this.state.otherEmploymentDialogBc) {
            return null;
        }
        const { storage } = this.props;

        const bindingContext = this.state.otherEmploymentDialogBc;
        const data = storage.getValue(bindingContext) as IPrAtsrFormSectionOtherEmploymentRecordsEntity;
        const attachment = data?.Attachment;

        const hasFiles = !isObjectEmpty(attachment);
        let fileViewer: ReactElement = null;
        if (hasFiles) {
            const mime = fileNameToMime(attachment.File.Name);
            const src = `${FILES_API_URL}/${attachment.File.Id}`;
            const fileViewerProps: IFileViewerProps = {
                src,
                fileName: attachment.File.Name,
                hiddenItems: [FileToolbarItem.Dashboard],
                onFileRemove: this.handleFileRemove,
                isReadOnly: storage.isReadOnly,
            };
            fileViewer = getFileViewer(mime, fileViewerProps);
        }

        return (
            <Dialog title={storage.t("AnnualTaxSettlementRequest:Form.OtherEmploymentDialogTitle")}
                    onClose={this.handleOtherEmploymentDialogClose}
                    onConfirm={null}
                    isEditableWindow
            >
                <SplitLayout paneStatus={this.state.paneStatus} onPaneStatusChanged={this.handlePaneStatusChange}>
                    <Pane visible={true} icon="Form" width="50%" isLast={false}>
                        <PlugAndPlayForm
                            getDef={getOtherEmploymentDefinition}
                            t={storage.t}
                            bindingContext={bindingContext}
                            formView={OtherEmploymentFormView}
                            data={data}
                            pageViewMode={storage.isReadOnly ? PageViewMode.FormReadOnly : PageViewMode.Default}
                            customFormViewProps={{
                                hasAttachment: hasFiles,
                                year: this.entity.Year
                            }}
                            formViewProps={{
                                hideButtons: false,
                                dialogProps: {
                                    title: storage.t("AnnualTaxSettlementRequest:Form.OtherEmploymentDialogTitle")
                                },
                                formProps: {
                                    title: storage.t("AnnualTaxSettlementRequest:Form.OtherEmploymentDialogTitle"),
                                    withoutPadding: false,
                                    hideHeader: false,
                                    shouldHideVariant: true,
                                    shouldHideAuditTrail: true
                                },
                                onAfterConfirm: this.handleOtherEmploymentDialogConfirm,
                                onClose: this.handleOtherEmploymentDialogClose
                            }}
                        />
                    </Pane>
                    <Pane visible={true} width={"50%"} icon={"DocumentFilled"}
                          showPaneGradient={hasFiles}>
                        {!hasFiles && <StyledView withoutPadding={false}>
                            <FileUploader style={{ paddingTop: "40px" }}
                                          busy={this.state.isUploadBusy}
                                          isReadOnly={storage.isReadOnly}
                                          isLocalDropArea
                                          onNewFiles={this.handleNewFiles}/>
                        </StyledView>}
                        {hasFiles && <>
                            <FileNameWrapper data-testid={TestIds.FileName}>{data.Attachment?.File?.Name}</FileNameWrapper>
                            <FileViewStyled hotspotContextId={"otherEmploymentAttachment"}>
                                {fileViewer}
                            </FileViewStyled>
                        </>}
                    </Pane>
                </SplitLayout>

            </Dialog>
        );
    }

    renderCustomDialogs(): React.ReactElement {
        return <>{this.renderOtherEmploymentDialog()}</>;
    }

    async save(saveArgs?: Partial<ISaveArgs>): Promise<IFormStorageSaveResult> {
        return await super.save({
            ...saveArgs,
            updateEnabledFor: [PrAnnualTaxSettlementRequestEntity.YearlyTaxpayerDeclaration]
        });
    }
}

export default withPermissionContext(AnnualTaxSettlementRequestFormView);